import { useCallback } from 'react';
import _ from 'lodash';
import { WidgetEventSuccessType, WidgetConfigs } from 'aerosync-web-sdk';

import useErnie from 'shared/hooks/use-ernie';
import { ERNIE_DEFAULT_MESSAGES, ERNIE_TIMEOUTS, ERNIE_TYPES } from 'shared/constants';
import { useDutchiePayAnalytics } from 'src/payments/hooks/use-dutchie-pay-analytics/use-dutchie-pay-analytics';

import { getLogger } from 'src/utils/datadog-logger';
import useDutchiePayEnrollment from 'src/hooks/use-dutchiePay-enrollment';
import { useCompleteAeroSyncConnection } from './use-complete-aerosync-connection';

const logger = getLogger('aerosync-widget');

export const useAerosyncEventHandlers = ({
  brandedDutchiePayName,
}: {
  brandedDutchiePayName: string;
}): {
  onClose: WidgetConfigs['onClose'];
  onSuccess: WidgetConfigs['onSuccess'];
  onError: WidgetConfigs['onError'];
  onEvent: WidgetConfigs['onEvent'];
  onLoad: WidgetConfigs['onLoad'];
} => {
  const showErnie = useErnie();
  const {
    trackAeroSyncOnEvent,
    trackAeroSyncOnLoad,
    trackAeroSyncOnSuccess,
    trackAeroSyncOnError,
    trackAeroSyncOnClose,
  } = useDutchiePayAnalytics();
  const { aggregatorLinkExited } = useDutchiePayEnrollment();
  const { onSuccessHandler } = useCompleteAeroSyncConnection({ brandedDutchiePayName });

  const onClose = useCallback(() => {
    trackAeroSyncOnClose();
    aggregatorLinkExited();
  }, [aggregatorLinkExited, trackAeroSyncOnClose]);

  const onSuccess = useCallback(
    (onSuccessPayload: WidgetEventSuccessType) => {
      trackAeroSyncOnSuccess(onSuccessPayload);
      void onSuccessHandler(onSuccessPayload);
    },
    [onSuccessHandler, trackAeroSyncOnSuccess]
  );

  const onError: WidgetConfigs['onError'] = useCallback(
    (error) => {
      trackAeroSyncOnError(error);
      logger.error('Aerosync widget error', { error });
      showErnie(ERNIE_DEFAULT_MESSAGES.PAY_BY_BANK, ERNIE_TYPES.DANGER, ERNIE_TIMEOUTS.SHORT);
    },
    [showErnie, trackAeroSyncOnError]
  );

  const onEvent: WidgetConfigs['onEvent'] = useCallback(trackAeroSyncOnEvent, [trackAeroSyncOnEvent]);

  const onLoad: WidgetConfigs['onLoad'] = useCallback(trackAeroSyncOnLoad, [trackAeroSyncOnLoad]);

  return {
    onClose,
    onSuccess,
    onError,
    onEvent,
    onLoad,
  };
};
